"use strict";
/**
 * Device picker for the landing page's screenshots section.
 *
 * Shapely 3D ships on three platforms (plus the iPhone Duo's unfolded screen,
 * which gets its own set), and stacking every set of screenshots on
 * top of each other made that section the longest part of the page. One set is
 * shown at a time instead, chosen by a segmented control.
 *
 * Two constraints shaped the implementation:
 *
 * - The tabs are authored as real links to `?device=<id>`, so with JavaScript
 *   disabled a click still works — it reloads the page, and the server serves
 *   the same static HTML, where this component (if it runs) or the default markup
 *   shows the right panel. Nothing here is required for the section to be
 *   readable.
 * - The selection lives in the URL rather than in localStorage, so a copied or
 *   refreshed URL reopens the same device. That is also why LanguagePicker
 *   carries `window.location.search` across a language switch.
 *
 * Mac is the default and is therefore the panel the markup leaves visible; the
 * default is never written into the URL, so the canonical `/en/` stays the
 * canonical URL for it.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.ScreenshotsPicker = void 0;
const DEVICES = ['mac', 'ipad', 'iphone-duo', 'iphone'];
const QUERY_PARAM = 'device';
const DEFAULT_DEVICE = 'mac';
const ACTIVE_CLASS = 'screenshots-tab-active';
const isDevice = (value) => DEVICES.includes(value);
class ScreenshotsPicker {
    tabs;
    panels;
    constructor(container) {
        this.tabs = Array.from(container.querySelectorAll('[data-device]'));
        this.panels = Array.from(document.querySelectorAll('[data-device-panel]'));
        this.tabs.forEach((tab) => {
            tab.addEventListener('click', (event) => {
                const device = tab.dataset.device ?? null;
                if (!isDevice(device))
                    return;
                // Upgrade the link in place: swapping panels client-side is the whole
                // point, so the navigation the href describes must not happen.
                event.preventDefault();
                this.select(device, true);
            });
        });
        const requested = new URLSearchParams(window.location.search).get(QUERY_PARAM);
        // Only an explicit, known value overrides the markup. An unknown one (a
        // typo, a stale link, a tracking parameter) leaves the Mac panel showing
        // rather than hiding them all.
        if (isDevice(requested) && requested !== DEFAULT_DEVICE) {
            this.select(requested, false);
        }
    }
    select(device, fromClick) {
        this.tabs.forEach((tab) => {
            const isActive = tab.dataset.device === device;
            tab.classList.toggle(ACTIVE_CLASS, isActive);
            tab.setAttribute('aria-selected', String(isActive));
        });
        // Tailwind's `hidden` utility, not the HTML attribute: the attribute is a
        // UA-stylesheet rule, which loses to the `grid` utility on the same element.
        this.panels.forEach((panel) => {
            panel.classList.toggle('hidden', panel.dataset.devicePanel !== device);
        });
        if (!fromClick)
            return;
        // replaceState, not pushState: the device is a view preference, not a
        // destination. Pushing would make the back button walk through every tab the
        // visitor tried instead of returning to the previous page.
        const url = new URL(window.location.href);
        if (device === DEFAULT_DEVICE) {
            url.searchParams.delete(QUERY_PARAM);
        }
        else {
            url.searchParams.set(QUERY_PARAM, device);
        }
        // The fragment is left exactly as it was — switching device must not move the
        // page, and the visitor is looking at this section already. `?device=` alone
        // is enough for a copied URL to reopen the same panel.
        window.history.replaceState(null, '', url);
        // Optional chaining: if the analytics script is blocked (ad blockers) or slow
        // to load, window.umami is undefined — tracking must never break the site.
        window.umami?.track('Screenshots Device', { device });
    }
}
exports.ScreenshotsPicker = ScreenshotsPicker;
